// ─────────────────────────────────────────────────────────────
// invoicePrint.config.js — Report 4: single sales invoice
// Shape: document (portrait)
// Data:  GET /api/tape/sales/:id  (header + items[])
//
// COGS never appears here. tape_sales_items carries cogs_amount,
// but that is internal — a customer copy must not disclose what
// the goods cost us.
//
// THE SIZE IS THE PRODUCT
// There is no catalogue, so a line has no name — it has a spec.
// The customer's own paperwork will say "Cloth Tape 69mm x 70yd",
// so that is what this prints.
//
// EMPTY ROWS ARE DROPPED, NOT DASHED
// A blank row prints as "—", which reads as "this was left
// blank" rather than "this does not apply". Filtering them out
// closes the gaps instead of advertising them.
// ─────────────────────────────────────────────────────────────

import { TERMS_INVOICE, SIGNATURES, FORMATTERS } from '../../print/printConfig';

const num = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));

// Trailing zeros off the numerics Postgres sends as strings —
// "69.00mm" on an invoice reads like a machine wrote it.
const trim = (v) => String(num(v));

export const invoicePrintConfig = (inv) => {
  const items = inv.items || [];

  const rows = items.map((it, i) => ({
    sr: i + 1,
    product: `${trim(it.width_mm)}mm × ${trim(it.length_yards)}yd`,
    description: [
      it.tape_type_name,
      it.micron_value ? `${trim(it.micron_value)} micron` : null,
    ]
      .filter(Boolean)
      .join(' · '),
    quantity: it.quantity,
    rate: it.rate,
    amount: it.amount,
  }));

  const totalPieces = items.reduce((s, it) => s + num(it.quantity), 0);
  const grandTotal  = num(inv.grand_total);

  const creditTerms =
    inv.customer_payment_type === 'credit'
      ? `Credit — ${inv.customer_payment_terms_days || 0} days`
      : 'Cash / On delivery';

  return {
    kind: 'document',
    orientation: 'p',
    title: 'Sales Invoice',

    boxes: [
      {
        heading: 'Bill To',
        rows: [
          { label: 'Customer', value: inv.customer_name },
          { label: 'Address', value: inv.customer_address },
          { label: 'Contact', value: inv.customer_contact_person },
                    { label: 'Phone', value: inv.customer_phone },
          {
            label: 'NTN / STRN',
            value: (inv.customer_ntn || inv.customer_strn)
              ? `${inv.customer_ntn || '—'} / ${inv.customer_strn || '—'}`
              : null,
          },
        ].filter(r => r.value),
      },
      {
        heading: 'Invoice Details',
        rows: [
          {
            label: 'Invoice #',
            value: inv.invoice_number,
            label2: 'Date',
            value2: FORMATTERS.date(inv.invoice_date),
          },
          {
            label: 'Lines',
            value: items.length,
            label2: 'Total Pieces',
            value2: FORMATTERS.int(totalPieces),
          },
          { label: 'Payment', value: creditTerms },
          { label: 'Notes', value: inv.notes },
        ].filter(r => r.value),
      },
    ],

    columns: [
      { key: 'sr', label: '#', align: 'center', fmt: 'int', width: 10 },
      { key: 'product', label: 'Size', align: 'left', fmt: 'text', width: 42 },
      { key: 'description', label: 'Description', align: 'left', fmt: 'text' },
      { key: 'quantity', label: 'Pieces', align: 'right', fmt: 'int', width: 18 },
      { key: 'rate', label: 'Rate', align: 'right', fmt: 'money2', width: 24 },
      { key: 'amount', label: 'Amount', align: 'right', fmt: 'money', width: 28, bold: true },
    ],

    rows,

    footRow: [
      '',
      'TOTAL',
      '',
      FORMATTERS.int(totalPieces),
      '',
      FORMATTERS.money(grandTotal),
    ],

    totals: [
            { label: 'Total Pieces', value: totalPieces, fmt: 'int', emphasis: true, tone: 'income' },
      { label: 'Grand Total', value: grandTotal, fmt: 'money', emphasis: true, tone: 'net' },
    ],

    terms: TERMS_INVOICE,
    signatures: SIGNATURES.invoice,
  };
};

export default invoicePrintConfig;